import { createApi } from '@reduxjs/toolkit/query/react';
import {
  femaStoredAlertRowToAlert,
  isFemaStoredAlertRow,
} from 'features/Weather/features/WeatherAlerts/features/FemaAlertList/utils/femaToAlert.adapter';
import { getAlertWithLocationDetails } from 'features/Weather/features/WeatherAlerts/utils/alertLocation.utils';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';
import { parseDateTime } from 'utils/dates/date.utils';

const EXPIRY_CHECK_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

export const alertsApi = createApi({
  reducerPath: 'alertsApi', // This is the name of the slice in the store
  baseQuery: axiosBaseQuery(), // Custom axios base query (calls apiClient with the query url and params)
  tagTypes: ['Alert', 'WPAlert', 'AlertHistory'], // Used to track when refetches are needed
  endpoints: (builder) => ({
    getAllActiveAlerts: builder.query({
      query: () => ({
        url: '/v2/alert',
        method: 'GET',
      }),
      transformResponse: (response) =>
        Array.isArray(response?.alerts)
          ? response.alerts.map((alert) =>
              isFemaStoredAlertRow(alert) ? femaStoredAlertRowToAlert(alert) : alert,
            )
          : [],
      providesTags: ['Alert'], // Tells RTK Query this query provides 'Alert' data and to refetch when invalidated
      // Polls every 3 minutes and removes alerts whose valid_until_time_utc has passed
      async onCacheEntryAdded(_arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          await cacheDataLoaded;

          const pruneExpired = () => {
            updateCachedData((draft) => {
              if (!Array.isArray(draft)) return;
              const now = new Date();
              const before = draft.length;
              const live = draft.filter((alert) => {
                const expiry = parseDateTime(alert.valid_until_time_utc);
                return expiry === null || expiry > now; // keep if unparseable or not yet expired
              });

              // Only mutate the draft (and trigger re-renders) when alerts were actually removed
              if (live.length !== before) {
                // console.debug(
                //   `[alertsApi] pruned ${before - live.length} expired alert(s) at ${now.toISOString()}`,
                // );
                draft.splice(0, draft.length, ...live);
              }
            });
          };

          // Run once immediately after data loads, then every 3 minutes
          pruneExpired();
          const intervalId = setInterval(pruneExpired, EXPIRY_CHECK_INTERVAL_MS);

          await cacheEntryRemoved;
          clearInterval(intervalId);
        } catch {
          // cacheDataLoaded rejected (no subscribers) — nothing to do
        }
      },
    }),
    postAlert: builder.mutation({
      query: (alert) => ({
        url: '/v2/alert/fusion-alert',
        method: 'POST',
        data: {
          polygon: alert.coordinates,
          city: alert.city,
          state: alert.state,
          broadcastTime: new Date().toISOString(),
          issuedTime: alert.issuedTime,
          expirationTime: alert.expirationTime,
          severity: alert.severity,
          headline: alert.headline,
          description: alert.description,
          type: alert.type,
          isInternalUse: alert.isInternalUse,
        },
      }),
    }),
    updateAlert: builder.mutation({
      query: ({ alertId, ...alert }) => ({
        url: `/v2/alert/fusion-alert/${alertId}`,
        method: 'PUT',
        data: {
          polygon: alert.coordinates,
          city: alert.city,
          state: alert.state,
          broadcastTime: new Date().toISOString(),
          issuedTime: alert.issuedTime,
          expirationTime: alert.expirationTime,
          severity: alert.severity,
          headline: alert.headline,
          description: alert.description,
          type: alert.type,
          isInternalUse: alert.isInternalUse,
        },
      }),
      invalidatesTags: ['Alert'],
    }),
    /**
     * getCWActiveAlerts gets Custom Weather Alerts
     * @param   {latitude, longitude}  [The lat long sent to server is from the Cookies]
     * @return  {[type]}  [return description]
     */
    getCWActiveAlerts: builder.query({
      query: (userCoordinates) => ({
        url: '/v2/alert/cw',
        method: 'GET',
      }),
      transformResponse: (response, meta, userCoordinates) => {
        if (!response?.alerts) return response;
        // Attach processed geospatial info to each alert
        return {
          ...response,
          alerts: response.alerts.map((alert) =>
            getAlertWithLocationDetails(alert, userCoordinates),
          ),
        };
      },
      providesTags: ['Alert'],
    }),
    setAlertEnd: builder.mutation({
      query: ({ alertId, notes }) => ({
        url: `/v2/alert/fusion-alert/${alertId}/end`,
        method: 'PATCH',
        data: {
          alertId,
          notes,
        },
      }),
      invalidatesTags: ['Alert'], // Tells RTK Query to refetch any queries that provide this tag
    }),
    getWorkplaceAlerts: builder.query({
      query: () => ({
        url: '/v2/alert/fusion',
        method: 'GET',
      }),
      providesTags: ['WPAlert'],
    }),
    get3DayAlertHistory: builder.query({
      query: () => ({
        url: '/v2/alert/cw/day-3',
        method: 'GET',
      }),
      providesTags: ['AlertHistory'],
    }),
    getFipsPolygon: builder.query({
      query: ({ fipsCode }) => ({
        url: `/v2/alert/polygon/${fipsCode}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetAllActiveAlertsQuery,
  usePostAlertMutation,
  useUpdateAlertMutation,
  useGetCWActiveAlertsQuery,
  useSetAlertEndMutation,
  useGetWorkplaceAlertsQuery,
  useGet3DayAlertHistoryQuery,
  useGetFipsPolygonQuery,
} = alertsApi;
